import { useState } from 'react';
import { Plus, Trophy, RefreshCw, Check, ListChecks, X, Edit2, Trash2, ChevronDown, ChevronUp, Undo2 } from 'lucide-react';
import {
  useTodayChores,
  useChoresLeaderboard,
  useCompleteChore,
  useUndoChoreCompletion,
  useCreateChore,
  useUpdateChore,
  useDeleteChore,
  useFamilyMembers,
} from '../api/hooks';
import { useToast } from '../context/ToastContext';
import { trackEvent } from '../lib/analytics';
import { ChoreCompletionModal } from '../components/ChoreCompletionModal';

const DAYS_OF_WEEK = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const DAYS_OF_WEEK_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAYS_OF_WEEK_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const WEEK_ORDINALS = [
  { value: 1, label: 'First' },
  { value: 2, label: 'Second' },
  { value: 3, label: 'Third' },
  { value: 4, label: 'Fourth' },
  { value: 5, label: 'Last' },
];
const CATEGORIES = ['Cleaning', 'Kitchen', 'Laundry', 'Outdoor', 'Pet Care', 'Organization', 'Other'];

interface ChoreFormData {
  title: string;
  description: string;
  recurrence: 'daily' | 'weekly' | 'monthly' | 'once';
  daysOfWeek: number[];
  dayOfMonth: number;
  // Advanced monthly: nth weekday (e.g., "2nd Tuesday")
  monthlyType: 'dayOfMonth' | 'nthWeekday';
  weekOfMonth: number;
  dayOfWeek: number;
  // Interval
  interval: number;
  // End conditions
  hasEndDate: boolean;
  endDate: string;
  // Exception dates
  exceptionDates: string[];
  newExceptionDate: string;
  // Other fields
  points: number;
  category: string;
  dueTime: string;
  assignedMemberIds: string[];
}

const defaultFormData: ChoreFormData = {
  title: '',
  description: '',
  recurrence: 'daily',
  daysOfWeek: [],
  dayOfMonth: 1,
  monthlyType: 'dayOfMonth',
  weekOfMonth: 1,
  dayOfWeek: 1,
  interval: 1,
  hasEndDate: false,
  endDate: '',
  exceptionDates: [],
  newExceptionDate: '',
  points: 10,
  category: '',
  dueTime: '',
  assignedMemberIds: [],
};

export function ChoresPage() {
  const [selectedPeriod, setSelectedPeriod] = useState<'week' | 'month' | 'all'>('week');
  const [showModal, setShowModal] = useState(false);
  const [editingChoreId, setEditingChoreId] = useState<string | null>(null);
  const [formData, setFormData] = useState<ChoreFormData>(defaultFormData);
  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const [completionModalChore, setCompletionModalChore] = useState<NonNullable<ReturnType<typeof useTodayChores>['data']>[number] | null>(null);
  const toast = useToast();

  const { data: todayChores, isLoading: choresLoading, refetch: refetchChores } = useTodayChores();
  const { data: leaderboard, isLoading: leaderboardLoading } = useChoresLeaderboard(selectedPeriod);
  const { data: members } = useFamilyMembers();
  const completeChore = useCompleteChore();
  const undoCompletion = useUndoChoreCompletion();
  const createChore = useCreateChore();
  const updateChore = useUpdateChore();
  const deleteChore = useDeleteChore();

  const handleToggleComplete = (choreId: string, memberId: string, isCompleted: boolean) => {
    if (isCompleted) {
      undoCompletion.mutate({ choreId, memberId });
    } else {
      completeChore.mutate({ choreId, memberId });
    }
  };

  /**
   * Handle member selection from completion modal
   */
  const handleModalMemberSelect = (memberId: string, isCompleted: boolean) => {
    if (!completionModalChore) return;
    handleToggleComplete(completionModalChore.id, memberId, isCompleted);
  };

  const openCreateModal = () => {
    setEditingChoreId(null);
    setFormData(defaultFormData);
    setShowMoreOptions(false);
    setShowModal(true);
    trackEvent('chore_add_modal_opened', { source: 'chores_page' });
  };

  const openEditModal = (chore: typeof todayChores extends (infer T)[] | undefined ? T : never) => {
    if (!chore) return;
    setEditingChoreId(chore.id);
    const config = chore.recurrenceConfig;
    const hasNthWeekday = config?.weekOfMonth !== undefined && config?.dayOfWeek !== undefined;
    setFormData({
      title: chore.title,
      description: chore.description || '',
      recurrence: chore.recurrence,
      daysOfWeek: config?.daysOfWeek || [],
      dayOfMonth: config?.dayOfMonth || 1,
      monthlyType: hasNthWeekday ? 'nthWeekday' : 'dayOfMonth',
      weekOfMonth: config?.weekOfMonth || 1,
      dayOfWeek: config?.dayOfWeek || 1,
      interval: config?.interval || 1,
      hasEndDate: !!config?.endDate,
      endDate: config?.endDate || '',
      exceptionDates: config?.exceptionDates || [],
      newExceptionDate: '',
      points: chore.points,
      category: chore.category || '',
      dueTime: chore.dueTime || '',
      assignedMemberIds: chore.assignedMembers.map((m) => m.id),
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingChoreId(null);
    setFormData(defaultFormData);
    setShowMoreOptions(false);
  };

  const handleSubmit = () => {
    if (!formData.title.trim()) return;

    // Build recurrence config based on recurrence type
    let recurrenceConfig: Record<string, unknown> | undefined;

    if (formData.recurrence === 'weekly') {
      recurrenceConfig = {
        daysOfWeek: formData.daysOfWeek,
        ...(formData.interval > 1 && { interval: formData.interval }),
      };
    } else if (formData.recurrence === 'monthly') {
      if (formData.monthlyType === 'nthWeekday') {
        recurrenceConfig = {
          weekOfMonth: formData.weekOfMonth,
          dayOfWeek: formData.dayOfWeek,
          ...(formData.interval > 1 && { interval: formData.interval }),
        };
      } else {
        recurrenceConfig = {
          dayOfMonth: formData.dayOfMonth,
          ...(formData.interval > 1 && { interval: formData.interval }),
        };
      }
    } else if (formData.recurrence === 'daily' && formData.interval > 1) {
      recurrenceConfig = { interval: formData.interval };
    }

    // Add end date if specified
    if (formData.hasEndDate && formData.endDate) {
      recurrenceConfig = { ...recurrenceConfig, endDate: formData.endDate };
    }

    // Add exception dates if any
    if (formData.exceptionDates.length > 0) {
      recurrenceConfig = { ...recurrenceConfig, exceptionDates: formData.exceptionDates };
    }

    const choreData = {
      title: formData.title.trim(),
      description: formData.description.trim() || undefined,
      recurrence: formData.recurrence,
      recurrenceConfig: recurrenceConfig && Object.keys(recurrenceConfig).length > 0 ? recurrenceConfig : undefined,
      points: formData.points,
      category: formData.category || undefined,
      dueTime: formData.dueTime || undefined,
      assignedMemberIds: formData.assignedMemberIds.length > 0 ? formData.assignedMemberIds : undefined,
    };

    if (editingChoreId) {
      updateChore.mutate({ id: editingChoreId, ...choreData }, { onSuccess: closeModal });
    } else {
      createChore.mutate(choreData, {
        onSuccess: () => {
          trackEvent('chore_created', {
            recurrence: formData.recurrence,
            assigned_member_count: formData.assignedMemberIds.length,
            used_advanced_options: showMoreOptions,
            points: formData.points,
          });
          toast.success('Chore created!');
          closeModal();
        },
      });
    }
  };

  const handleQuickSubmit = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && formData.title.trim() && !editingChoreId) {
      e.preventDefault();
      trackEvent('chore_quick_created', {
        recurrence: formData.recurrence,
        had_weekly_days: formData.daysOfWeek.length > 0,
      });
      handleSubmit();
    }
  };

  const handleDelete = (choreId: string) => {
    if (confirm('Are you sure you want to delete this chore?')) {
      deleteChore.mutate(choreId);
    }
  };

  const toggleDayOfWeek = (day: number) => {
    setFormData((prev) => ({
      ...prev,
      daysOfWeek: prev.daysOfWeek.includes(day)
        ? prev.daysOfWeek.filter((d) => d !== day)
        : [...prev.daysOfWeek, day].sort((a, b) => a - b),
    }));
  };

  const toggleMemberAssignment = (memberId: string) => {
    setFormData((prev) => ({
      ...prev,
      assignedMemberIds: prev.assignedMemberIds.includes(memberId)
        ? prev.assignedMemberIds.filter((id) => id !== memberId)
        : [...prev.assignedMemberIds, memberId],
    }));
  };

  const addExceptionDate = () => {
    if (formData.newExceptionDate && !formData.exceptionDates.includes(formData.newExceptionDate)) {
      setFormData((prev) => ({
        ...prev,
        exceptionDates: [...prev.exceptionDates, prev.newExceptionDate].sort(),
        newExceptionDate: '',
      }));
    }
  };

  const removeExceptionDate = (date: string) => {
    setFormData((prev) => ({
      ...prev,
      exceptionDates: prev.exceptionDates.filter((d) => d !== date),
    }));
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="bg-bg-surface border-b border-border-default px-6 py-5">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-display-sm font-semibold text-text-primary">Chores</h1>
          <div className="flex items-center gap-3">
            <button
              onClick={() => refetchChores()}
              className="btn-icon hover:bg-bg-secondary"
              title="Refresh"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
            <button onClick={openCreateModal} className="btn btn-primary rounded-xl shadow-teal hover:shadow-lg transition-all duration-200">
              <Plus className="w-5 h-5 mr-2" />
              Add Chore
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Chore List */}
          <div className="lg:col-span-2 space-y-4">
            <h2 className="font-display text-xl font-semibold text-text-primary">Today's Chores</h2>

            {choresLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-teal" />
              </div>
            ) : !todayChores || todayChores.length === 0 ? (
              <div className="card rounded-2xl text-center py-12">
                <div className="w-16 h-16 rounded-2xl bg-teal/10 flex items-center justify-center mx-auto mb-4">
                  <ListChecks className="w-8 h-8 text-teal" />
                </div>
                <h3 className="font-display text-lg font-semibold text-text-primary mb-1">No chores for today</h3>
                <p className="text-text-secondary">Add chores to get started with task tracking</p>
              </div>
            ) : (
              <div className="space-y-4">
                {todayChores.map((chore) => (
                  <div
                    key={chore.id}
                    className={`card rounded-2xl border-2 border-border-light ${chore.allCompleted ? 'opacity-60 bg-bg-secondary/30' : 'bg-white'}`}
                  >
                    {/* Chore Header */}
                    <div className="flex items-center gap-3 mb-3">
                      <div className="flex-1">
                        <div className={`font-semibold ${chore.allCompleted ? 'line-through text-text-muted' : 'text-text-primary'}`}>
                          {chore.title}
                        </div>
                        <div className="flex items-center gap-2 mt-1.5">
                          {chore.category && (
                            <span className="text-xs px-2.5 py-1 rounded-lg bg-bg-secondary text-text-secondary font-medium">
                              {chore.category}
                            </span>
                          )}
                          <span className="text-xs text-amber font-semibold">
                            {chore.points} pts
                          </span>
                          <span className="text-xs text-text-muted capitalize font-medium">
                            {chore.recurrence}
                          </span>
                        </div>
                        {/* Show "Completed by" badge for daily chores */}
                        {chore.recurrence === 'daily' && chore.completedByMember && (
                          <div className="mt-2 px-3 py-2 bg-sage/10 border border-sage/30 rounded-lg flex items-center gap-2">
                            <div
                              className="w-5 h-5 rounded-md flex-shrink-0"
                              style={{ backgroundColor: chore.completedByMember.memberColor }}
                            />
                            <span className="text-xs text-sage font-medium">
                              Completed by {chore.completedByMember.memberName}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => openEditModal(chore)}
                          className="btn-icon w-9 h-9 rounded-xl hover:bg-bg-secondary"
                          title="Edit chore"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        {/* Undo button for daily chores that have been completed */}
                        {chore.recurrence === 'daily' && chore.completedByMember && (
                          <button
                            onClick={() => handleToggleComplete(chore.id, chore.completedByMember!.memberId, true)}
                            disabled={undoCompletion.isPending}
                            className="btn-icon w-9 h-9 rounded-xl text-amber hover:bg-amber/10"
                            title={`Undo completion by ${chore.completedByMember.memberName}`}
                          >
                            <Undo2 className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(chore.id)}
                          className="btn-icon w-9 h-9 rounded-xl text-coral hover:bg-coral/10"
                          title="Delete chore"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Assignee Completion List - sorted alphabetically */}
                    {chore.assignedMembers.length > 0 && (
                      <div className="border-t border-border-light pt-4 space-y-3">
                        {chore.assignedMembers.length > 1 ? (
                          // Multi-member chore: show "Mark complete" button to open modal
                          <button
                            onClick={() => setCompletionModalChore(chore)}
                            disabled={completeChore.isPending || undoCompletion.isPending}
                            className="w-full flex items-center justify-center gap-2 p-3 rounded-xl bg-teal/10 hover:bg-teal/20 transition-colors text-teal font-semibold"
                          >
                            <Check className="w-4 h-4" />
                            Mark complete
                          </button>
                        ) : (
                          // Single member: direct toggle
                          [...chore.assignedMembers].map((member) => (
                            <button
                              key={member.id}
                              onClick={() => handleToggleComplete(chore.id, member.id, member.completed)}
                              disabled={completeChore.isPending || undoCompletion.isPending}
                              className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-bg-secondary transition-colors"
                            >
                              <div
                                className={`w-7 h-7 rounded-lg border-2 flex items-center justify-center transition-all duration-200 ${
                                  member.completed
                                    ? 'bg-sage border-sage text-white'
                                    : 'border-border-default hover:border-teal'
                                }`}
                              >
                                {member.completed && <Check className="w-4 h-4" />}
                              </div>
                              <div
                                className="w-9 h-9 rounded-xl flex items-center justify-center text-xs font-semibold text-white"
                                style={{ backgroundColor: member.color }}
                              >
                                {member.name.substring(0, 2)}
                              </div>
                              <span className={`flex-1 text-left font-medium ${member.completed ? 'text-text-muted line-through' : 'text-text-primary'}`}>
                                {member.name}
                              </span>
                              {member.completed && (
                                <span className="text-xs font-semibold text-sage">+{chore.points} pts</span>
                              )}
                            </button>
                          ))
                        )}

                        {/* Show completion status for multi-member chores */}
                        {chore.assignedMembers.length > 1 && (
                          <div className="flex flex-wrap gap-2 pt-1">
                            {[...chore.assignedMembers].sort((a, b) => a.name.localeCompare(b.name)).map((member) => (
                              <div
                                key={member.id}
                                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium ${
                                  member.completed
                                    ? 'bg-sage/20 text-sage'
                                    : 'bg-bg-secondary text-text-muted'
                                }`}
                              >
                                <div
                                  className="w-5 h-5 rounded-md flex items-center justify-center text-[9px] font-semibold text-white"
                                  style={{ backgroundColor: member.color }}
                                >
                                  {member.name.charAt(0)}
                                </div>
                                <span>{member.name}</span>
                                {member.completed && <Check className="w-3 h-3" />}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Leaderboard */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-semibold text-text-primary flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-amber/20 flex items-center justify-center">
                  <Trophy className="w-4 h-4 text-amber" />
                </div>
                Leaderboard
              </h2>
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value as 'week' | 'month' | 'all')}
                className="text-sm font-medium border-2 border-border-default rounded-xl px-3 py-2 focus:outline-none focus:border-teal transition-colors"
              >
                <option value="week">This Week</option>
                <option value="month">This Month</option>
                <option value="all">All Time</option>
              </select>
            </div>

            {leaderboardLoading ? (
              <div className="card rounded-2xl flex items-center justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-teal" />
              </div>
            ) : !leaderboard || leaderboard.length === 0 ? (
              <div className="card rounded-2xl text-center py-8">
                <div className="w-14 h-14 rounded-2xl bg-amber/10 flex items-center justify-center mx-auto mb-3">
                  <Trophy className="w-7 h-7 text-amber" />
                </div>
                <p className="text-text-secondary font-medium">No points earned yet</p>
              </div>
            ) : (
              <div className="card rounded-2xl space-y-2 p-4">
                {leaderboard.map((entry, idx) => (
                  <div
                    key={entry.member?.id || idx}
                    className={`flex items-center gap-3 p-3 rounded-xl ${idx === 0 ? 'bg-amber/10' : idx % 2 === 0 ? 'bg-bg-secondary/50' : ''}`}
                  >
                    <div className={`text-sm font-bold w-7 h-7 rounded-lg flex items-center justify-center ${idx === 0 ? 'bg-amber text-white' : 'bg-bg-secondary text-text-muted'}`}>
                      {idx + 1}
                    </div>
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center font-semibold border-2 text-sm"
                      style={{
                        backgroundColor: `${entry.member?.color || '#E5E7EB'}15`,
                        borderColor: entry.member?.color || '#E5E7EB',
                        color: entry.member?.color || '#5A7A8C'
                      }}
                    >
                      {entry.member?.name.substring(0, 2) || '??'}
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-text-primary">{entry.member?.name || 'Unknown'}</div>
                      <div className="flex items-center gap-2 text-sm">
                        <span className="font-semibold text-amber">{entry.totalPoints} pts</span>
                        <span className="text-text-muted">({entry.completionCount} chores)</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Chore Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-bg-surface rounded-2xl shadow-elevated w-full max-w-md max-h-[90vh] overflow-y-auto animate-fadeIn">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-border-default">
              <h2 className="font-display text-xl font-semibold text-text-primary">
                {editingChoreId ? 'Edit Chore' : 'Add New Chore'}
              </h2>
              <button onClick={closeModal} className="btn-icon -mr-2 rounded-xl hover:bg-bg-secondary">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-5 space-y-5">
              {/* Title - Always shown */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Chore name
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
                  onKeyDown={handleQuickSubmit}
                  className="input w-full"
                  placeholder="e.g., Take out trash"
                  autoFocus
                />
                {!editingChoreId && (
                  <p className="text-xs text-text-muted mt-1.5">Press Enter to quick add</p>
                )}
              </div>

              {/* === ADD MODE: Simplified form === */}
              {!editingChoreId && (
                <>
                  {/* Recurrence Buttons */}
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Repeats
                    </label>
                    <div className="flex gap-2">
                      {(['daily', 'weekly', 'monthly', 'once'] as const).map((type) => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => setFormData((prev) => ({ ...prev, recurrence: type }))}
                          className={`flex-1 py-2.5 text-sm font-medium rounded-md border transition-colors ${
                            formData.recurrence === type
                              ? 'bg-teal border-teal text-white'
                              : 'border-border-default text-text-secondary hover:bg-gray-50'
                          }`}
                        >
                          {type === 'once' ? 'Once' : type.charAt(0).toUpperCase() + type.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Days of Week (for weekly in add mode) */}
                  {formData.recurrence === 'weekly' && (
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        On these days
                      </label>
                      <div className="grid grid-cols-7 gap-1.5">
                        {DAYS_OF_WEEK.map((day, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => toggleDayOfWeek(idx)}
                            title={DAYS_OF_WEEK_LABELS[idx]}
                            className={`aspect-square text-sm font-medium rounded-md border transition-colors ${
                              formData.daysOfWeek.includes(idx)
                                ? 'bg-teal border-teal text-white'
                                : 'border-border-default text-text-secondary hover:bg-gray-50'
                            }`}
                          >
                            {day}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Assign to - Compact avatar row */}
                  {members && members.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Assign to
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {members.map((member) => (
                          <button
                            key={member.id}
                            type="button"
                            onClick={() => toggleMemberAssignment(member.id)}
                            title={member.name}
                            className={`flex items-center gap-2 px-2 py-1.5 rounded-md border transition-all ${
                              formData.assignedMemberIds.includes(member.id)
                                ? 'border-teal bg-teal/10 ring-1 ring-teal'
                                : 'border-border-default hover:bg-gray-50'
                            }`}
                          >
                            <div
                              className="w-6 h-6 rounded-md flex items-center justify-center text-xs font-medium text-white"
                              style={{ backgroundColor: member.color }}
                            >
                              {member.name.substring(0, 1)}
                            </div>
                            <span className="text-sm text-text-primary">{member.name}</span>
                            {formData.assignedMemberIds.includes(member.id) && (
                              <Check className="w-3.5 h-3.5 text-teal" />
                            )}
                          </button>
                        ))}
                      </div>
                      {formData.assignedMemberIds.length === 0 && (
                        <p className="text-xs text-text-muted mt-1.5">Anyone can complete if unassigned</p>
                      )}
                    </div>
                  )}

                  {/* More Options Toggle */}
                  <div className="pt-1">
                    <button
                      type="button"
                      onClick={() => {
                        setShowMoreOptions(!showMoreOptions);
                        if (!showMoreOptions) {
                          trackEvent('chore_add_advanced_expanded');
                        }
                      }}
                      className="flex items-center gap-2 text-sm text-text-secondary hover:text-text-primary transition-colors"
                    >
                      {showMoreOptions ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                      <span className="font-medium">
                        {showMoreOptions ? 'Less options' : 'More options'}
                      </span>
                    </button>
                  </div>

                  {/* Expanded Options - Points only */}
                  {showMoreOptions && (
                    <div className="pt-4 border-t border-border-light">
                      <div className="flex items-center gap-4">
                        <label className="text-sm font-medium text-text-primary">
                          Points
                        </label>
                        <input
                          type="number"
                          min="1"
                          max="100"
                          value={formData.points}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, points: parseInt(e.target.value) || 10 }))
                          }
                          className="input w-20"
                        />
                        <span className="text-xs text-text-muted">Default is 10</span>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* === EDIT MODE: Full form === */}
              {editingChoreId && (
                <>
                  {/* Description */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                  className="input w-full"
                  rows={2}
                  placeholder="Optional details..."
                />
              </div>

              {/* Recurrence */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">
                  Recurrence
                </label>
                <select
                  value={formData.recurrence}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      recurrence: e.target.value as ChoreFormData['recurrence'],
                    }))
                  }
                  className="input w-full"
                >
                  <option value="once">One time</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>

              {/* Days of Week (for weekly) */}
              {formData.recurrence === 'weekly' && (
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Days of Week
                  </label>
                  <div className="flex gap-1">
                    {DAYS_OF_WEEK.map((day, idx) => (
                      <button
                        key={day}
                        type="button"
                        onClick={() => toggleDayOfWeek(idx)}
                        className={`px-3 py-1.5 text-sm rounded-lg border-2 transition-all duration-200 ${
                          formData.daysOfWeek.includes(idx)
                            ? 'bg-teal border-teal text-white'
                            : 'border-border-default text-text-secondary hover:bg-bg-secondary'
                        }`}
                      >
                        {day}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Monthly Options */}
              {formData.recurrence === 'monthly' && (
                <div className="space-y-3">
                  {/* Monthly Type Toggle */}
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Repeat on
                    </label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setFormData((prev) => ({ ...prev, monthlyType: 'dayOfMonth' }))}
                        className={`flex-1 px-3 py-2 text-sm rounded-xl border-2 transition-all duration-200 ${
                          formData.monthlyType === 'dayOfMonth'
                            ? 'bg-teal border-teal text-white'
                            : 'border-border-default text-text-secondary hover:bg-bg-secondary'
                        }`}
                      >
                        Day of Month
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData((prev) => ({ ...prev, monthlyType: 'nthWeekday' }))}
                        className={`flex-1 px-3 py-2 text-sm rounded-xl border-2 transition-all duration-200 ${
                          formData.monthlyType === 'nthWeekday'
                            ? 'bg-teal border-teal text-white'
                            : 'border-border-default text-text-secondary hover:bg-bg-secondary'
                        }`}
                      >
                        Nth Weekday
                      </button>
                    </div>
                  </div>

                  {/* Day of Month Selector */}
                  {formData.monthlyType === 'dayOfMonth' && (
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-1">
                        Day of Month
                      </label>
                      <select
                        value={formData.dayOfMonth}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, dayOfMonth: parseInt(e.target.value) }))
                        }
                        className="input w-full"
                      >
                        {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                          <option key={day} value={day}>
                            {day}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Nth Weekday Selector */}
                  {formData.monthlyType === 'nthWeekday' && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-1">
                          Which
                        </label>
                        <select
                          value={formData.weekOfMonth}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, weekOfMonth: parseInt(e.target.value) }))
                          }
                          className="input w-full"
                        >
                          {WEEK_ORDINALS.map((ord) => (
                            <option key={ord.value} value={ord.value}>
                              {ord.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-1">
                          Day
                        </label>
                        <select
                          value={formData.dayOfWeek}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, dayOfWeek: parseInt(e.target.value) }))
                          }
                          className="input w-full"
                        >
                          {DAYS_OF_WEEK_FULL.map((day, idx) => (
                            <option key={day} value={idx}>
                              {day}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Interval (for recurring types) */}
              {formData.recurrence !== 'once' && (
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">
                    Repeat every
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="1"
                      max="12"
                      value={formData.interval}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, interval: Math.max(1, parseInt(e.target.value) || 1) }))
                      }
                      className="input w-20"
                    />
                    <span className="text-text-secondary">
                      {formData.recurrence === 'daily' && (formData.interval === 1 ? 'day' : 'days')}
                      {formData.recurrence === 'weekly' && (formData.interval === 1 ? 'week' : 'weeks')}
                      {formData.recurrence === 'monthly' && (formData.interval === 1 ? 'month' : 'months')}
                    </span>
                  </div>
                </div>
              )}

              {/* End Date (for recurring types) */}
              {formData.recurrence !== 'once' && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="checkbox"
                      id="hasEndDate"
                      checked={formData.hasEndDate}
                      onChange={(e) => setFormData((prev) => ({ ...prev, hasEndDate: e.target.checked }))}
                      className="w-4 h-4 rounded border-border-default"
                    />
                    <label htmlFor="hasEndDate" className="text-sm font-medium text-text-primary">
                      Set end date
                    </label>
                  </div>
                  {formData.hasEndDate && (
                    <input
                      type="date"
                      value={formData.endDate}
                      onChange={(e) => setFormData((prev) => ({ ...prev, endDate: e.target.value }))}
                      className="input w-full"
                    />
                  )}
                </div>
              )}

              {/* Exception Dates (for recurring types) */}
              {formData.recurrence !== 'once' && (
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Skip dates (exceptions)
                  </label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="date"
                      value={formData.newExceptionDate}
                      onChange={(e) => setFormData((prev) => ({ ...prev, newExceptionDate: e.target.value }))}
                      className="input flex-1"
                    />
                    <button
                      type="button"
                      onClick={addExceptionDate}
                      disabled={!formData.newExceptionDate}
                      className="btn btn-secondary px-3"
                    >
                      Add
                    </button>
                  </div>
                  {formData.exceptionDates.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {formData.exceptionDates.map((date) => (
                        <span
                          key={date}
                          className="inline-flex items-center gap-1 px-2 py-1 text-sm bg-gray-100 rounded-lg"
                        >
                          {date}
                          <button
                            type="button"
                            onClick={() => removeExceptionDate(date)}
                            className="text-text-muted hover:text-red-500"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Points and Category row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">
                    Points
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={formData.points}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, points: parseInt(e.target.value) || 10 }))
                    }
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">
                    Category
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData((prev) => ({ ...prev, category: e.target.value }))}
                    className="input w-full"
                  >
                    <option value="">None</option>
                    {CATEGORIES.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Due Time */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">
                  Due Time
                </label>
                <input
                  type="time"
                  value={formData.dueTime}
                  onChange={(e) => setFormData((prev) => ({ ...prev, dueTime: e.target.value }))}
                  className="input w-full"
                />
              </div>

              {/* Assigned Members */}
              {members && members.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Assign to Members
                  </label>
                  <div className="space-y-2">
                    {members.map((member) => (
                      <button
                        key={member.id}
                        type="button"
                        onClick={() => toggleMemberAssignment(member.id)}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all duration-200 ${
                          formData.assignedMemberIds.includes(member.id)
                            ? 'border-teal bg-teal/10'
                            : 'border-border-default hover:bg-bg-secondary'
                        }`}
                      >
                        <div
                          className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center ${
                            formData.assignedMemberIds.includes(member.id)
                              ? 'bg-teal border-teal'
                              : 'border-border-default'
                          }`}
                        >
                          {formData.assignedMemberIds.includes(member.id) && (
                            <Check className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <div
                          className="w-9 h-9 rounded-xl flex items-center justify-center text-xs font-semibold text-white"
                          style={{ backgroundColor: member.color }}
                        >
                          {member.name.substring(0, 2)}
                        </div>
                        <span className="text-text-primary">{member.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
                </>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 px-6 py-5 border-t border-border-default">
              <button onClick={closeModal} className="btn btn-secondary rounded-xl">
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!formData.title.trim() || createChore.isPending || updateChore.isPending}
                className="btn btn-primary rounded-xl"
              >
                {createChore.isPending || updateChore.isPending
                  ? 'Saving...'
                  : editingChoreId
                    ? 'Save Changes'
                    : 'Create Chore'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Chore completion modal */}
      {completionModalChore && (
        <ChoreCompletionModal
          choreId={completionModalChore.id}
          choreTitle={completionModalChore.title}
          chorePoints={completionModalChore.points}
          choreRecurrence={completionModalChore.recurrence}
          completedByMember={completionModalChore.completedByMember}
          members={completionModalChore.assignedMembers}
          onSelectMember={handleModalMemberSelect}
          onClose={() => setCompletionModalChore(null)}
          source="chores_page"
          isPending={completeChore.isPending || undoCompletion.isPending}
        />
      )}
    </div>
  );
}
